import { PolylineSimplifier } from "../../geometry";
import { VectorTileGeomType } from "./tiles.vector.interfaces";
import { IsExpressionSpecification, LayerStyleTypes, } from "./tiles.vector.style.interface";
export class TileVectorRenderer {
    constructor(style, simplifier) {
        this._style = style;
        this._simplifier = simplifier ?? PolylineSimplifier.Shared;
        this._orderedStyleLayers = this._prepareOrderedLayers(style);
    }
    renderTile(tile, ctx, w, h, style) {
        w = w ?? ctx.canvas.width;
        h = h ?? ctx.canvas.height;
        if (tile) {
            style = style ?? this._style;
            if (style) {
                if (style !== this._style) {
                    this._style = style;
                    this._orderedStyleLayers = this._prepareOrderedLayers(style);
                }
                if (this._orderedStyleLayers) {
                    for (const key of this._orderedStyleLayers) {
                        const styleLayer = style.layers[key];
                        const name = styleLayer.sourceLayer;
                        const layer = tile.layers[name];
                        if (layer && this._acceptLayer(name, layer, styleLayer)) {
                            this._drawLayer(ctx, name, layer, w, h, styleLayer);
                        }
                    }
                }
            }
        }
    }
    _prepareOrderedLayers(style) {
        if (!style) {
            return undefined;
        }
        const a = {};
        const defaultSlot = this._generateUniqueSlotName();
        const length = style.layers.length;
        for (let i = 0; i < length; i++) {
            const value = style.layers[i];
            if (!value) {
                continue;
            }
            const slot = value.slot ?? defaultSlot;
            let group = a[slot];
            if (!group) {
                a[slot] = group = [];
            }
            group.push(i);
        }
        return Object.values(a).flat();
    }
    _generateUniqueSlotName() {
        return `slot-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }
    _drawLayer(ctx, key, layer, w, h, styleLayer) {
        const extent = layer.extent;
        const scalex = w / extent;
        const scaley = h / extent;
        ctx.save();
        try {
            switch (styleLayer.type) {
                case LayerStyleTypes.Fill: {
                    const paint = styleLayer.paint;
                    if (!paint) {
                        break;
                    }
                    const color = this._evaluate(paint.color);
                    const outlineColor = this._evaluate(paint.outlineColor);
                    if (!color && !outlineColor) {
                        break;
                    }
                    if (color) {
                        ctx.fillStyle = color;
                    }
                    if (outlineColor) {
                        ctx.strokeStyle = outlineColor;
                        ctx.lineWidth = TileVectorRenderer.DefaultLineWith;
                    }
                    const opacity = this._evaluate(paint.opacity) ?? TileVectorRenderer.DefaultOpacity;
                    ctx.globalAlpha = opacity;
                    const translate = this._evaluate(paint.translate);
                    for (let i = 0; i < layer.length; i++) {
                        const feature = layer.feature(i);
                        if (feature && this._acceptFeature(feature, styleLayer)) {
                            switch (feature.type) {
                                case VectorTileGeomType.POLYGON: {
                                    const transformed = this._getTransformedGeometry(feature, scalex, scaley, translate);
                                    ctx.beginPath();
                                    this._drawPath(ctx, transformed[0]);
                                    if (transformed.length > 1) {
                                        for (let i = 1; i < transformed.length; i++) {
                                            this._drawPath(ctx, transformed[i]);
                                        }
                                    }
                                    ctx.fill();
                                    if (outlineColor) {
                                        ctx.stroke();
                                    }
                                    break;
                                }
                                default:
                                    break;
                            }
                        }
                    }
                    break;
                }
                case LayerStyleTypes.Line: {
                    const paint = styleLayer.paint;
                    if (!paint) {
                        break;
                    }
                    const color = this._evaluate(paint.color);
                    if (!color) {
                        break;
                    }
                    ctx.strokeStyle = color;
                    const opacity = this._evaluate(paint.opacity) ?? TileVectorRenderer.DefaultOpacity;
                    ctx.globalAlpha = opacity;
                    const width = this._evaluate(paint.width);
                    ctx.lineWidth = width ?? TileVectorRenderer.DefaultLineWith;
                    const dasharray = this._evaluate(paint.dashArray);
                    if (dasharray) {
                        ctx.setLineDash(dasharray);
                    }
                    const translate = this._evaluate(paint.translate);
                    for (let i = 0; i < layer.length; i++) {
                        const feature = layer.feature(i);
                        if (feature && this._acceptFeature(feature, styleLayer)) {
                            switch (feature.type) {
                                case VectorTileGeomType.POLYGON:
                                case VectorTileGeomType.LINESTRING: {
                                    const transformed = this._getTransformedGeometry(feature, scalex, scaley, translate);
                                    for (const line of transformed) {
                                        ctx.beginPath();
                                        this._drawPath(ctx, line);
                                        ctx.stroke();
                                    }
                                    break;
                                }
                                default:
                                    break;
                            }
                        }
                    }
                    break;
                }
                case LayerStyleTypes.Circle: {
                    const paint = styleLayer.paint;
                    if (!paint) {
                        break;
                    }
                    const color = this._evaluate(paint.color);
                    const outlineColor = this._evaluate(paint.strockeColor);
                    if (!color && !outlineColor) {
                        break;
                    }
                    if (color) {
                        ctx.fillStyle = color;
                    }
                    const opacity = this._evaluate(paint.opacity) ?? TileVectorRenderer.DefaultOpacity;
                    ctx.globalAlpha = opacity;
                    if (outlineColor) {
                        ctx.strokeStyle = outlineColor;
                        ctx.lineWidth = TileVectorRenderer.DefaultLineWith;
                    }
                    const translate = this._evaluate(paint.translate);
                    for (let i = 0; i < layer.length; i++) {
                        const feature = layer.feature(i);
                        if (feature && this._acceptFeature(feature, styleLayer)) {
                            switch (feature.type) {
                                case VectorTileGeomType.POINT: {
                                    const transformed = this._getTransformedGeometry(feature, scalex, scaley, translate);
                                    for (const point of transformed) {
                                        ctx.beginPath();
                                        ctx.arc(point[0].x, point[0].y, paint.radius, 0, 2 * Math.PI);
                                        ctx.fill();
                                        if (outlineColor) {
                                            ctx.stroke();
                                        }
                                    }
                                    break;
                                }
                                default: {
                                    break;
                                }
                            }
                        }
                    }
                    break;
                }
                default: {
                    break;
                }
            }
        }
        finally {
            ctx.restore();
        }
    }
    _evaluate(p) {
        if (IsExpressionSpecification(p)) {
            return this._evaluateExpression(p);
        }
        return p;
    }
    _evaluateExpression(p) {
        return undefined;
    }
    _getTransformedGeometry(feature, scalex, scaley, translate) {
        const geom = feature.loadGeometry();
        const transformed = [];
        for (let i = 0; i < geom.length; i++) {
            for (let j = 0; j < geom[i].length; j++) {
                if (translate) {
                    geom[i][j].x += translate[0];
                    geom[i][j].y += translate[1];
                }
                geom[i][j].x *= scalex;
                geom[i][j].y *= scaley;
            }
            transformed.push(this._simplifier.simplify(geom[i]));
        }
        return transformed;
    }
    _drawPolyline(ctx, points) {
        this._drawPath(ctx, points);
    }
    _drawPolygon(ctx, points) { }
    _drawPath(ctx, points) {
        let i = 0;
        let p = points[i];
        ctx.moveTo(p.x, p.y);
        if (i < points.length - 1) {
            do {
                p = points[++i];
                ctx.lineTo(p.x, p.y);
            } while (i < points.length - 1);
        }
    }
    _acceptLayer(key, layer, styleLayer) {
        return true;
    }
    _acceptFeature(layer, styleLayer) {
        return true;
    }
}
TileVectorRenderer.DefaultOpacity = 1.0;
TileVectorRenderer.DefaultLineWith = 1.0;
TileVectorRenderer.DefaultColor = "white";
//# sourceMappingURL=tiles.vector.renderer.js.map